import { gql } from '@apollo/client';

export const CRM_CALENDAR_BOOKING_FIELDS = `
    _id
    accountId
    contactId
    adminId
    title
    googleEventId
    meetLink
    htmlLink
    startAt
    endAt
    timeZone
    attendeeEmail
    status
    source
    attendance
    meetingTypeId
    cancelledAt
    createdAt
`;

/* Booking-link Google connection (POGO-13214 Phase 1) — a SECOND, separate
   per-staff Google connection (free/busy + event insert) with disconnect. */
export const GET_CRM_BOOKING_GOOGLE_AUTH_URL = gql`
    query GetCrmBookingGoogleAuthUrl {
        getCrmBookingGoogleAuthUrl {
            url
        }
    }
`;

export const GET_CRM_BOOKING_GOOGLE_CONNECTION = gql`
    query GetCrmBookingGoogleConnection {
        getCrmBookingGoogleConnection {
            connected
            googleEmail
            connectedAt
        }
    }
`;

/* Team-member schedule (POGO-13214 Phase 2) — the current admin's weekly
   working hours + timezone for meeting booking links. */
export const CRM_SCHEDULE_FIELDS = `
    configured
    timeZone
    weeklyHours {
        mon { start end }
        tue { start end }
        wed { start end }
        thu { start end }
        fri { start end }
        sat { start end }
        sun { start end }
    }
`;

export const GET_MY_CRM_SCHEDULE = gql`
    query GetMyCrmSchedule {
        getMyCrmSchedule {
            ${CRM_SCHEDULE_FIELDS}
        }
    }
`;

/* Booking calendar page (POGO-13214) — a chosen user's schedule, and every
   configured schedule for the calendar view. Admin-managed. */
export const GET_CRM_SCHEDULE_FOR_ADMIN = gql`
    query GetCrmScheduleForAdmin($adminId: ID!) {
        getCrmScheduleForAdmin(adminId: $adminId) {
            ${CRM_SCHEDULE_FIELDS}
        }
    }
`;

export const LIST_CRM_SCHEDULES = gql`
    query ListCrmSchedules {
        listCrmSchedules {
            adminId
            ${CRM_SCHEDULE_FIELDS}
        }
    }
`;

/* Admin blocks (POGO-13214) — company-wide unavailable periods on the calendar. */
export const CRM_ADMIN_BLOCK_FIELDS = `
    _id
    title
    startAt
    endAt
    allDay
    blocksBooking
    adminId
    note
    color
    seriesId
    createdAt
    updatedAt
`;

export const LIST_CRM_ADMIN_BLOCKS = gql`
    query ListCrmAdminBlocks {
        listCrmAdminBlocks {
            ${CRM_ADMIN_BLOCK_FIELDS}
        }
    }
`;

/* Meeting types (POGO-13214 Phase 3) — the object a booking link exposes. */
export const CRM_MEETING_TYPE_FIELDS = `
    _id
    type
    name
    description
    durationMinutes
    color
    hostAdminIds
    ccAdminIds
    bufferBeforeMinutes
    bufferAfterMinutes
    minNoticeHours
    maxDaysAhead
    confirmationTemplateId
    cancellationTemplateId
    reminders {
        templateId
        hoursBefore
    }
    status
    createdAt
    updatedAt
`;

export const LIST_CRM_MEETING_TYPES = gql`
    query ListCrmMeetingTypes {
        listCrmMeetingTypes {
            ${CRM_MEETING_TYPE_FIELDS}
        }
    }
`;

/* Host / CC picker — every admin with their booking-connection status. */
export const LIST_CRM_MEETING_HOSTS = gql`
    query ListCrmMeetingHosts {
        listCrmMeetingHosts {
            _id
            name
            email
            bookingConnected
        }
    }
`;

/* Booking links (POGO-13214 Phase 4) — the shareable slug that exposes a set
   of meeting types on the public /book/:slug page, with a copyable URL and an
   iframe embed snippet. */
export const CRM_BOOKING_LINK_FIELDS = `
    _id
    name
    slug
    meetingTypeIds
    hostAdminIds
    redirectUrl
    status
    publicUrl
    embedSnippet
    createdAt
    updatedAt
`;

export const LIST_CRM_BOOKING_LINKS = gql`
    query ListCrmBookingLinks {
        listCrmBookingLinks {
            ${CRM_BOOKING_LINK_FIELDS}
        }
    }
`;

/* Meetings agenda (POGO-13214 Phase 8) — the upcoming booking-link meetings a
   staff member can mark Attended / DNA / Cancel. */
export const CRM_MEETING_AGENDA_FIELDS = `
    _id
    accountId
    contactId
    hostAdminId
    hostName
    meetingTypeId
    meetingTypeName
    title
    meetLink
    startAt
    endAt
    timeZone
    bookerName
    bookerEmail
    bookerBusinessName
    note
    attendance
    status
    ccAdminIds
    attendeeContacts {
        _id
        firstName
        lastName
        email
    }
    seriesId
`;

/* The agenda slice the staff member is viewing — Upcoming (default), Past, or
   Cancelled (POGO-13309). `$scope` maps to the API's CrmMeetingListScope enum. */
export const LIST_CRM_MEETINGS = gql`
    query ListCrmMeetings($scope: CrmMeetingListScope!) {
        listCrmMeetings(scope: $scope) {
            ${CRM_MEETING_AGENDA_FIELDS}
        }
    }
`;

/* Team members' own Google-calendar events — read-only overlay on the calendar. */
export const LIST_CRM_GOOGLE_EVENTS = gql`
    query ListCrmGoogleEvents($from: DateTime!, $to: DateTime!, $adminIds: [ID!]) {
        listCrmGoogleEvents(from: $from, to: $to, adminIds: $adminIds) {
            adminId
            googleEventId
            title
            startAt
            endAt
            allDay
            color
        }
    }
`;

/* Company-wide calendar display settings (visible day window). */
export const GET_CRM_CALENDAR_SETTINGS = gql`
    query GetCrmCalendarSettings {
        getCrmCalendarSettings {
            dayStartHour
            dayEndHour
        }
    }
`;

/* Meetings within a date range — powers the calendar (any week, incl. past). */
export const LIST_CRM_MEETINGS_IN_RANGE = gql`
    query ListCrmMeetingsInRange($from: DateTime!, $to: DateTime!) {
        listCrmMeetingsInRange(from: $from, to: $to) {
            ${CRM_MEETING_AGENDA_FIELDS}
        }
    }
`;
